import React, { useState, useCallback, useEffect } from 'react';
import { Case, QueueConfiguration, Opinion, SubjectImagePreferences, isAllFilterOff, getFilterOnState } from 'services';
import {
  Stack,
  IconButton,
  IIconProps,
  IStackStyles,
  Checkbox,
  getTheme,
  StackItem,
  DocumentCard
} from '@fluentui/react';
import { CaseContent } from './CaseContent';
import CardOpinionBanner from './CardOpinionBanner';
import { StyledTag } from '.';

const expandIcon: IIconProps = { iconName: 'miniExpand' };
const viewIcon: IIconProps = { iconName: 'View' };
const hideIcon: IIconProps = { iconName: 'Hide2' };
const infoIcon: IIconProps = { iconName: 'Info' };
export interface CaseCardProps {
  case: Case;
  checked?: boolean;
  config: QueueConfiguration;
  settings: SubjectImagePreferences;
  opinion?: Opinion;
  reportedTrait?: string;
  traitCount?: number;
  index?: number;
  setZoomInIsChecked?: (boolean) => void;
  onChange?: (checked) => void;
  onZoomClick: (item: Case, num?: number) => void;
  onUndoClick?: () => void;
}

export const CaseCard: React.FunctionComponent<CaseCardProps> = props => {
  const { palette, fonts } = getTheme();
  const isFilterOff = isAllFilterOff(props.settings);
  const isReviewed = props.opinion !== undefined;
  const [enableSettings, setEnableSettings] = useState<boolean>(isFilterOff);
  const [displayCaseDetails, setDisplayCaseDetails] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean | undefined>(props.checked);
  const filterIconViewText = enableSettings ? 'Show preview' : 'Hide preview';
  const filterIconInfoText = !displayCaseDetails ? 'More info' : 'Hide info';
  const iconBackground = displayCaseDetails ? 'rgb(242, 242, 243)' : '';
  const onContentClick = useCallback(() => {
    props.onChange && props.onChange(!props.checked);
  }, [props]);
  // A minimum set of filters to apply, if the global state has no filters then we default to all on.
  const currentFilterSet = isFilterOff ? getFilterOnState(props.settings) : props.settings;
  const content = (
    <CaseContent
      case={props.case}
      config={props.config}
      settings={!enableSettings ? undefined : currentFilterSet}
      className="position-absolute"
      onClick={onContentClick}
      checked={props.checked}
    />
  );
  const evidenceStackStyle: IStackStyles = {
    root: {
      overflowWrap: 'break-word',
      wordBreak: 'break-word'
    }
  };
  const evidenceDisplay = (): JSX.Element[] => {
    let caseEvidenceItem: [string, string][] = [];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    caseEvidenceItem = Object.keys(props.case.caseEvidence).map(key => {
      return [key, props.case.caseEvidence[key]];
    });
    return (
      caseEvidenceItem &&
      caseEvidenceItem.map(
        (row: [string, string | number], index): JSX.Element => {
          if (index < 5) {
            return (
              <Stack horizontal key={row[0]} className="pb-2 px-2">
                <StackItem grow className="font-weight-light text-nowrap">
                  {row[0]}
                </StackItem>
                <StackItem className="text-right" styles={evidenceStackStyle}>
                  {row[1]}
                </StackItem>
              </Stack>
            );
          }

          return <></>;
        }
      )
    );
  };
  const caseDetailsContent = (
    <div className="h-100 w-100 position-absolute p-2 overflow-auto">
      {props.case.caseEvidence && evidenceDisplay()}
    </div>
  );
  const cardStyle: Partial<IStackStyles> = {
    root: {
      borderRadius: 4,
      borderWidth: 0,
      maxWidth: '100%',
      boxShadow: '0px 1.6px 3.6px 0px rgba(0, 0, 0, 0.13), 0px 0.3px 0.9px 0px rgba(0, 0, 0, 0.11)'
    }
  };

  const checkBoxStyle = {
    label: { margin: '0' },
    checkbox: { margin: 0, height: 16, width: 16 },
    root: { borderRadius: 2, padding: 1, background: palette.white, paddingBottom: 5 }
  };

  const contentSectionSize: Partial<IStackStyles> = {
    root: {
      flexGrow: 1,
      overflow: 'auto',
      position: 'relative',
      minHeight: props.config.uxPreferences.maxCardHeight || 300
    }
  };
  const iconSectionStyle: Partial<IStackStyles> = {
    root: { borderTop: '1px solid', borderColor: palette.neutralLight, borderRadius: '0 0 4px 4px', zIndex: 10 }
  };
  const rootStackStyle: IStackStyles = {
    root: {
      borderRadius: '4px 4px 0 0',
      paddingBottom: 2,
      zIndex: 10,
      height: 33,
      borderBottom: '1px solid',
      borderColor: palette.neutralLight,
      background: props.config.uxPreferences.showCardTitleGradient
        ? 'linear-gradient(rgba(0, 0, 0,0.3), rgba(255,0,0,0))'
        : undefined
    }
  };

  useEffect(() => {
    setEnableSettings(!isAllFilterOff(props.settings));
    setIsChecked(props.checked);
  }, [props.settings, props.checked]);

  return (
    <div className="position-relative p-0">
      <DocumentCard styles={cardStyle}>
        <Stack.Item>
          {props.opinion !== undefined && <CardOpinionBanner opinion={props.opinion} onUndoClick={props.onUndoClick} />}
          <Stack horizontal className="w-100 px-2 pt-2" styles={rootStackStyle}>
            <Stack.Item grow align="center">
              {(props.reportedTrait && <StyledTag tag={props.reportedTrait} />) || ' '}
              {(props.reportedTrait && <StyledTag traitCount={props.traitCount} className="ml-1" />) || ' '}
            </Stack.Item>
            {props.checked !== undefined && (
              <Stack.Item align="center">
                <Checkbox
                  checked={isChecked}
                  ariaLabel={`CaseCard ${props.index ?? ''} checkbox`}
                  styles={checkBoxStyle}
                  onChange={(ev, checked?: boolean): void => {
                    setIsChecked(checked);
                    props.onChange && props.onChange(checked);
                  }}
                  disabled={isReviewed}
                />
              </Stack.Item>
            )}
          </Stack>
        </Stack.Item>
        <Stack.Item styles={contentSectionSize}>
          {!isReviewed && !displayCaseDetails && content}
          {!isReviewed && displayCaseDetails && caseDetailsContent}
        </Stack.Item>
        <Stack.Item styles={iconSectionStyle}>
          <Stack horizontal>
            <Stack.Item grow>
              <IconButton
                styles={{ icon: { fontSize: fonts.large.fontSize } }}
                iconProps={enableSettings ? viewIcon : hideIcon}
                title={filterIconViewText}
                ariaLabel={`CaseCard ${props.index ?? ''} ${filterIconViewText}`}
                onClick={(): void => {
                  setEnableSettings(!enableSettings);
                }}
              />
              {props.case.caseEvidence && Object.keys(props.case.caseEvidence).length > 0 && (
                <IconButton
                  styles={{ icon: { fontSize: fonts.large.fontSize } }}
                  style={{ backgroundColor: iconBackground }}
                  iconProps={infoIcon}
                  title={filterIconInfoText}
                  ariaLabel={`CaseCard ${props.index ?? ''} ${filterIconInfoText}`}
                  onClick={(): void => {
                    setDisplayCaseDetails(!displayCaseDetails);
                  }}
                />
              )}
            </Stack.Item>
            <Stack.Item>
              {props.config.uxPreferences.showCardZoomOption && (
                <IconButton
                  styles={{ icon: { fontSize: fonts.large.fontSize } }}
                  iconProps={expandIcon}
                  title="Expand"
                  ariaLabel={`CaseCard ${props.index ?? ''} expand`}
                  onClick={(): void => {
                    props.setZoomInIsChecked && props.setZoomInIsChecked(isChecked);
                    props.onZoomClick(props.case, props.index);
                  }}
                />
              )}
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </DocumentCard>
    </div>
  );
};
